import type { DownloadFilesOptions, ZipFileEntry } from './downloadFilesAsZip';

/**
 * 一括ダウンロード（zip / フォルダ保存）で使う、途中で切れても続きから取り直すファイル取得。
 *
 * 数 GB のフォルダは取得に数時間かかり、その間の一瞬の回線断・スリープ復帰・署名付き URL の
 * 失効（1 時間）で 1 ファイルの受信が途中で止まる。zip は 1 本のストリームなので、
 * そのまま失敗させると全体がやり直しになる。ここでは受信済みのバイト位置から
 * Range 指定で取り直し、呼び出し側には途切れのない 1 本のストリームとして渡す。
 */

/** 1 回の接続で、進捗なしに続けて失敗してよい回数 */
const MAX_ATTEMPTS = 6;

/** 再試行までの待ち時間（回数ごと） */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];

/** オフライン中に回線の復帰を待つ最長時間 */
const OFFLINE_WAIT_MAX_MS = 10 * 60 * 1000;

/** 署名付き URL の失効とみなすステータス（S3 は期限切れで 403、署名不正で 400 を返す） */
const EXPIRED_STATUSES = new Set([400, 403]);

/** 取り直しても変わらないステータス */
const PERMANENT_STATUSES = new Set([404, 410]);

/** URL の一覧を取り直す最短間隔（失効が連続したときに一覧 API を叩き続けないため） */
const REFRESH_MIN_INTERVAL_MS = 5 * 60 * 1000;

/** ディスクへ書き出す一括ダウンロード（zip / フォルダ保存）の共通オプション */
export interface BulkFetchOptions extends DownloadFilesOptions {
  /**
   * 署名付き URL を取り直す（失効対策）。呼ぶたびに最新の URL で entries を返す。
   * 大きいフォルダは取得に 1 時間（URL の有効期限）以上かかることがあるため、
   * 失効した時点で取り直して同じパスのファイルを再取得する。
   */
  refreshEntries?: () => Promise<ZipFileEntry[]>;
  /** 受信が途切れて再接続しているあいだ true で呼ばれる（画面の「再接続中」表示用） */
  onReconnectingChange?: (reconnecting: boolean) => void;
}

/** ファイルごとの再接続の開始 / 終了を数え、1 件以上再接続している間だけ onChange(true) にする */
export const reconnectTracker = (onChange?: (reconnecting: boolean) => void) => {
  let count = 0;
  return (reconnecting: boolean) => {
    const before = count > 0;
    count += reconnecting ? 1 : -1;
    if (before !== count > 0) onChange?.(count > 0);
  };
};

export const urlOfEntry = (entry: ZipFileEntry): string | null =>
  'file' in entry ? entry.file.download_url || entry.file.original_url : null;

export const sizeOfEntry = (entry: ZipFileEntry): number | null =>
  'file' in entry ? entry.file.size ?? null : entry.blob.size;

/**
 * ファイルごとの最新の URL を管理する。失効を検出したら一覧を取り直し（同時に失効した
 * 複数ファイルで 1 回にまとめる）、以後はすべてのファイルで新しい URL を使う。
 */
export interface UrlResolver {
  current(entry: ZipFileEntry): string | null;
  /** failedUrl が失効したときの新しい URL。取り直せなければ null */
  refresh(entry: ZipFileEntry, failedUrl: string): Promise<string | null>;
}

export const createUrlResolver = (
  refreshEntries?: () => Promise<ZipFileEntry[]>,
): UrlResolver => {
  let latest = new Map<string, string>();
  let refreshing: Promise<void> | null = null;
  let lastRefreshAt = 0;

  const current = (entry: ZipFileEntry) => latest.get(entry.path) ?? urlOfEntry(entry);

  const refresh = async (entry: ZipFileEntry, failedUrl: string) => {
    const known = latest.get(entry.path);
    if (known && known !== failedUrl) return known;
    if (!refreshEntries) return null;
    if (!refreshing) {
      if (Date.now() - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) return null;
      lastRefreshAt = Date.now();
      refreshing = refreshEntries()
        .then((fresh) => {
          latest = new Map(
            fresh.flatMap((e) => {
              const url = urlOfEntry(e);
              return url ? [[e.path, url] as const] : [];
            }),
          );
        })
        .finally(() => {
          refreshing = null;
        });
    }
    await refreshing.catch(() => undefined);
    const fresh = latest.get(entry.path);
    return fresh && fresh !== failedUrl ? fresh : null;
  };

  return { current, refresh };
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitUntilOnline = (): Promise<void> => {
  if (typeof navigator === 'undefined' || navigator.onLine) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = setTimeout(done, OFFLINE_WAIT_MAX_MS);
    window.addEventListener('online', done);
  });
};

export interface OpenedEntry {
  stream: ReadableStream<Uint8Array>;
  lastModified?: Date;
}

export interface OpenEntryOptions {
  /** 受信が途切れて再接続を始めた / 終えたときに呼ばれる（画面の「再接続中」表示用） */
  onReconnect?: (reconnecting: boolean) => void;
}

interface Connection {
  body: ReadableStream<Uint8Array>;
  /** Range が無視されて先頭から返ってきたときに読み捨てるバイト数 */
  skip: number;
  size: number | null;
  lastModified?: Date;
}

/**
 * entry を開き、途中で切れたら続きから取り直すストリームを返す。
 * 最初の接続が再試行しても成功しなければ null（呼び出し側でそのファイルを失敗扱いにする）。
 * 途中の取り直しが尽きた場合はストリームがエラーになる。
 */
export async function openResumableEntry(
  entry: ZipFileEntry,
  resolver: UrlResolver,
  options?: OpenEntryOptions,
): Promise<OpenedEntry | null> {
  if ('blob' in entry) return { stream: entry.blob.stream() };

  let url = resolver.current(entry);
  if (!url) return null;

  const connect = async (offset: number): Promise<Connection | null> => {
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await sleep(BACKOFF_MS[Math.min(attempt - 1, BACKOFF_MS.length - 1)]);
        await waitUntilOnline();
      }
      let response: Response;
      try {
        response = await fetch(url!, {
          cache: 'no-store',
          headers: offset > 0 ? { Range: `bytes=${offset}-` } : undefined,
        });
      } catch {
        continue; // 回線断など。待って取り直す
      }
      if (response.ok && response.body) {
        const length = Number(response.headers.get('content-length'));
        const lastModified = response.headers.get('last-modified');
        return {
          body: response.body,
          skip: offset > 0 && response.status === 200 ? offset : 0,
          size: response.status === 200 && length > 0 ? length : null,
          lastModified: lastModified ? new Date(lastModified) : undefined,
        };
      }
      if (EXPIRED_STATUSES.has(response.status)) {
        const fresh = await resolver.refresh(entry, url!);
        if (!fresh) return null;
        url = fresh;
        attempt -= 1; // 失効は回線の失敗に数えない
        continue;
      }
      if (PERMANENT_STATUSES.has(response.status)) return null;
    }
    return null;
  };

  const first = await connect(0);
  if (!first) return null;

  const size = first.size ?? sizeOfEntry(entry);
  let reader = first.body.getReader();
  let skip = first.skip;
  let offset = 0;
  // データが 1 バイトも進まないまま続けて再接続した回数（接続はできるがすぐ切れる場合に打ち切る）
  let stalls = 0;

  const reconnect = async (): Promise<boolean> => {
    reader.cancel().catch(() => undefined);
    stalls += 1;
    if (stalls > MAX_ATTEMPTS) return false;
    options?.onReconnect?.(true);
    try {
      if (stalls > 1) await sleep(BACKOFF_MS[Math.min(stalls - 2, BACKOFF_MS.length - 1)]);
      const next = await connect(offset);
      if (!next) return false;
      reader = next.body.getReader();
      skip = next.skip;
      return true;
    } finally {
      options?.onReconnect?.(false);
    }
  };

  const stream = new ReadableStream<Uint8Array>({
    async pull(controller) {
      for (;;) {
        let chunk: Awaited<ReturnType<typeof reader.read>>;
        try {
          chunk = await reader.read();
        } catch {
          if (await reconnect()) continue;
          controller.error(new Error(`Download interrupted: ${entry.path}`));
          return;
        }
        if (chunk.done) {
          // 接続が正常終了に見えても、サイズに届いていなければ途中で切れている
          if (size !== null && offset < size) {
            if (await reconnect()) continue;
            controller.error(new Error(`Download interrupted: ${entry.path}`));
            return;
          }
          controller.close();
          return;
        }
        let value = chunk.value;
        if (skip > 0) {
          const drop = Math.min(skip, value.byteLength);
          skip -= drop;
          // subarray（同じバッファのビュー）だと client-zip がバッファ全体を書き込むためコピーする
          value = value.slice(drop);
          if (value.byteLength === 0) continue;
        }
        offset += value.byteLength;
        stalls = 0;
        controller.enqueue(value);
        return;
      }
    },
    cancel(reason) {
      return reader.cancel(reason);
    },
  });

  return { stream, lastModified: first.lastModified };
}
